/**
 * Image URL helpers that route Lovable Cloud (Supabase) storage URLs through
 * the on-the-fly image transformation endpoint to deliver right-sized,
 * format-negotiated (WebP/AVIF) thumbnails for card/list views.
 *
 * Non-storage URLs (lovable assets, local imports, external) pass through
 * unchanged.
 */

type ResizeMode = "cover" | "contain" | "fill";

interface TransformOpts {
  width: number;
  height?: number;
  quality?: number;
  resize?: ResizeMode;
}

const OBJECT_SEGMENT = "/storage/v1/object/public/";
const RENDER_SEGMENT = "/storage/v1/render/image/public/";

function isStorageUrl(url: string): boolean {
  return typeof url === "string" && url.includes(OBJECT_SEGMENT);
}

export function getOptimizedImageUrl(
  url: string | null | undefined,
  opts: TransformOpts,
): string {
  if (!url) return "";
  if (!isStorageUrl(url)) return url;

  const { width, height, quality = 75, resize = "cover" } = opts;
  const base = url.replace(OBJECT_SEGMENT, RENDER_SEGMENT);
  const params = new URLSearchParams();
  params.set("width", String(Math.round(width)));
  if (height) params.set("height", String(Math.round(height)));
  params.set("quality", String(quality));
  params.set("resize", resize);
  return `${base}?${params.toString()}`;
}

/**
 * Build a `srcset` string for the given widths. Returns empty string if the
 * URL is not transformable (so callers can omit the attribute).
 */
export function getImageSrcSet(
  url: string | null | undefined,
  widths: number[],
  opts: Omit<TransformOpts, "width"> = {},
): string {
  if (!url || !isStorageUrl(url)) return "";
  return widths
    .map((w) => `${getOptimizedImageUrl(url, { ...opts, width: w })} ${w}w`)
    .join(", ");
}
